import React from 'react';
import MaisAcoes, { ItemDeMenu } from './MaisAcoes';
import Icon from './Icon';

/**
 * Cabeçalho único das superfícies de DETALHE do HUB (§8 do guia visual).
 *
 * O guia padronizou listagem (casca de página, stat cards, tabela, ListToolbar)
 * mas nunca padronizou o que aparece DEPOIS do clique. Resultado: cada
 * superfície de detalhe nasceu no seu momento e ficaram três anatomias
 * diferentes no mesmo módulo — o painel do Encontrar com a ação primária no
 * rodapé, a página de trabalho do Cadastrar com ela no topo, e o drawer de
 * criar proposta com outra estrutura ainda. Navegar de uma pra outra parecia
 * trocar de sistema.
 *
 * A regra que este componente carrega:
 *   [ ‹ voltar ]  Identidade              MÉTRICA   [ Ação primária ]  ⋯
 *                 fila de badges
 *
 * O CONTAINER continua livre (drawer para decidir sem perder a lista, página
 * para trabalhar por dias) — o que não muda é a cabeça.
 */

/** O item do ⋯ (ver MaisAcoes). Mantido o nome para quem já importa. */
export type PanelMenuItem = ItemDeMenu;

export interface PanelHeaderProps {
  /** Volta pra origem (listagem). Só nas superfícies que NAVEGAM (página). */
  onBack?: () => void;
  backLabel?: string;
  /** Texto ou elemento (a ficha da fatura põe o nome dentro do ClientPopover). */
  title: React.ReactNode;
  titleTooltip?: string;
  /** Quebra o título em linhas em vez de cortar com reticências. A confirmação liga: o título dela é a PERGUNTA
   *  ("Excluir o lead CERTEZZA CONSULTORIA…?"), e cortada ela some justo no nome do que vai ser afetado. */
  quebrarTitulo?: boolean;
  /** Identificação em MONO sob o título (código · razão social · CNPJ), §10.4 do
   *  guia. Opcional: as superfícies de detalhe do §8 não usam. */
  subtitle?: React.ReactNode;
  /** Fila de badges/metadados. Use os tons de `statusColors.ts`. */
  badges?: React.ReactNode;
  /** O número que importa nesta tela (valor estimado, total da proposta…). */
  metric?: { label: string; value: string; className?: string };
  /** UMA ação primária laranja por superfície. */
  primary?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    title?: string;
    icon?: string;
    loading?: boolean;
  };
  /** Botões neutros (links pro portal, edital…) — ficam antes da métrica. */
  secondary?: React.ReactNode;
  /** Vai pro ⋯. Ação destrutiva mora aqui, nunca solta no cabeçalho. */
  menuItems?: PanelMenuItem[];
  /** Fecha a superfície. Só em drawer/modal. */
  onClose?: () => void;
  className?: string;
}

const PanelHeader: React.FC<PanelHeaderProps> = ({
  onBack,
  backLabel = 'Voltar',
  title,
  titleTooltip,
  quebrarTitulo = false,
  subtitle,
  badges,
  metric,
  primary,
  secondary,
  menuItems,
  onClose,
  className = '',
}) => {


  const hasMenu = !!menuItems && menuItems.length > 0;

  return (
    <div className={`flex items-start justify-between gap-3 flex-wrap p-4 border-b border-central-borda ${className}`}>
      <div className="min-w-0 flex-1 flex items-start gap-3">
        {onBack && (
          <button
            onClick={onBack}
            className="shrink-0 mt-0.5 flex items-center gap-1.5 px-3 py-2 bg-central-elevado hover:bg-central-cartao border border-central-borda rounded-lg text-sm text-central-texto transition-colors"
            title={backLabel}
          >
            <Icon name="chevronLeft" className="w-4 h-4" /> {backLabel}
          </button>
        )}
        <div className="min-w-0">
          <h2 className={`text-lg font-bold text-central-texto ${quebrarTitulo ? 'break-words' : 'truncate'}`} title={titleTooltip || (typeof title === 'string' ? title : undefined)}>
            {title}
          </h2>
          {subtitle && <p className="text-xs text-central-texto-fraco font-mono truncate">{subtitle}</p>}
          {badges && (
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-central-texto-fraco mt-0.5">{badges}</div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {secondary}

        {metric && (
          <div className="text-right mr-1">
            <div className="text-[10px] text-central-texto-fraco uppercase tracking-wide">{metric.label}</div>
            <div className={`text-lg font-bold leading-tight ${metric.className || 'text-central-texto'}`}>
              {metric.value}
            </div>
          </div>
        )}

        {primary && (
          <button
            onClick={primary.onClick}
            disabled={primary.disabled || primary.loading}
            title={primary.title || primary.label}
            className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {primary.icon && (
              <Icon name={primary.loading ? 'refresh' : primary.icon} className={`w-4 h-4 ${primary.loading ? 'animate-spin' : ''}`} />
            )}
            {primary.label}
          </button>
        )}

        {hasMenu && <MaisAcoes itens={menuItems!} />}

        {onClose && (
          <button onClick={onClose} className="text-central-texto-fraco hover:text-central-texto p-1" title="Fechar">
            <Icon name="x" className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PanelHeader;
